import { PickerCell, type PickOption } from "./InterviewCells";

/**
 * s221 Lane 1, mechanism 7 — A PICKER WHOSE CHOICE SHOWS A CONSEQUENCE (SPEC: `pickNotes`).
 *
 * Some answers change what happens next, and the planner should see that the moment they choose:
 * "Named in your will? — No" says a court decides who raises the children until the will says so.
 * The dropdown is the interview's board-drawn PickerCell (InterviewCells.tsx, the named G7 exception); the note is its own
 * piece because it is a paragraph — it renders under the row, full width, not inside a narrow cell.
 *
 * A dropdown's change IS the answer, so it commits on change with the chosen value; there is no blur
 * to wait for and no state read that could lag a tick behind.
 */

export type ConsequenceNotes = Readonly<Partial<Record<string, string>>>;

/** The note for a stored choice, or null. Pure. */
export function consequenceFor(notes: ConsequenceNotes | undefined, value: string): string | null {
  if (!notes || !value) return null;
  return notes[value] ?? null;
}

export function ConsequencePicker({
  value,
  options,
  onCommit,
  ariaLabel,
  placeholder,
  testId = "interview-pick",
}: {
  value: string;
  options: readonly PickOption[];
  onCommit: (value: string) => void;
  ariaLabel?: string;
  /** The board's `ph` (s222 BOARD-AS-SPEC-1); PickerCell's own default when absent. */
  placeholder?: string;
  testId?: string;
}) {
  return (
    <PickerCell value={value} options={options} onCommit={onCommit} ariaLabel={ariaLabel} placeholder={placeholder} testId={testId} />
  );
}

export function ConsequenceNote({
  notes,
  value,
  testId = "interview-pick-note",
}: {
  notes: ConsequenceNotes | undefined;
  value: string;
  testId?: string;
}) {
  const note = consequenceFor(notes, value);
  if (!note) return null;
  return (
    <p className="mb-3 rounded-[8px] bg-warm-stone-light px-4 py-3 text-[14px] leading-relaxed text-warm-charcoal" data-testid={testId}>
      {note}
    </p>
  );
}

// ── s234 (D-234.94) — one note for the rows it covers ───────────────────────────────────────────────────────────────
// A note that is IDENTICAL for several rows of one table shows ONCE under the table, naming the rows it covers
// ("Emily and James: their share is held for them until they turn 18…"); a note only one row carries stays under that
// row. Shared by every table with per-row notes (G7) — the screens compute each row's note and hand it here.

export type RowNote = { rowId: string; name: string; text: string | null };

/** "Emily", "Emily and James", "Ann, Bob and Cy". Pure. */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Group the rows' notes: identical texts on two or more rows become one, worded with the rows' names; the rest stay on
 *  their own row. Order is the rows' order. Pure. */
export function groupRowNotes(notes: readonly RowNote[]): { shared: { rowIds: string[]; text: string }[]; ownRow: Set<string> } {
  const byText = new Map<string, RowNote[]>();
  for (const n of notes) if (n.text) byText.set(n.text, [...(byText.get(n.text) ?? []), n]);
  const shared: { rowIds: string[]; text: string }[] = [];
  const ownRow = new Set<string>();
  for (const [text, rows] of Array.from(byText.entries())) {
    if (rows.length < 2) { ownRow.add(rows[0].rowId); continue; }
    const lowered = text.charAt(0).toLowerCase() + text.slice(1);
    shared.push({ rowIds: rows.map((r) => r.rowId), text: `${joinNames(rows.map((r) => r.name))}: ${lowered}` });
  }
  return { shared, ownRow };
}

/** The shared notes, drawn once each under the table in the row note's own look. */
export function SharedRowNotes({ notes, testIdPrefix }: { notes: readonly { text: string }[]; testIdPrefix: string }) {
  if (notes.length === 0) return null;
  return (
    <div className="mt-3 flex flex-col">
      {notes.map((n, i) => (
        <p key={i} className="mb-3 rounded-[8px] bg-warm-stone-light px-4 py-3 text-[14px] leading-relaxed text-warm-charcoal" data-testid={`${testIdPrefix}-shared-note-${i}`}>
          {n.text}
        </p>
      ))}
    </div>
  );
}
